/** @jsx jsx */
import React, { useState } from 'react';
import { css, jsx } from '@compiled/react';
import Button from '@atlaskit/button';
import LoadingButton from '@atlaskit/button/loading-button';
import AtlaskitHeading from '@atlaskit/heading';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import AkModalDialog, {
	ModalBody,
	ModalHeader,
	ModalTitle,
	ModalFooter,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import Select, { type ActionMeta, type ValueType } from '@atlaskit/select';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAnalyticsEvents,
	ContextualAnalyticsData,
	FireScreenAnalytics,
	MODAL,
} from '@atlassian/jira-product-analytics-bridge';
import { usePageContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-context/main.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import {
	useIsFetchingDraftDetails,
	useUserCapabilities,
} from '../../../controllers/store/index.tsx';
import messages from './messages.tsx';

type CreateArticleModalProps = {
	onCancel: () => void;
	topicId?: string;
	topicName?: string;
};

type OptionType = {
	label: string;
	value: string;
};

export const MODAL_WIDTH = `${gridSize * 51}px`;

export const CreateArticleModal = (props: CreateArticleModalProps) => {
	const { formatMessage } = useIntl();
	const { onCancel, topicId, topicName } = props;
	const [routerState, routerActions] = useRouter();
	const [selectedSpaceKey, updateSelectedSpaceKey] = useState<string>('');

	const [{ data: pageContextData }] = usePageContext();
	const { projectContext } = pageContextData || {};
	const { projectKey, projectId } = projectContext || {};
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [isFetchingDraftDetails, { fetchDraftAndNavigateToEditor }] = useIsFetchingDraftDetails();
	const [{ data: userPermissionData }] = useUserCapabilities();
	const {
		spaceUserCapabilities: { editableSpaces },
	} = userPermissionData;
	const fetchDraftAndNavigateToRoute = async () => {
		fetchDraftAndNavigateToEditor({
			spaceKey: selectedSpaceKey,
			routerActions,
			// @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
			projectKey,
			// @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number'.
			projectId,
			onCloseRedirectUrl: routerState.location.pathname,
			analyticsEvent: createAnalyticsEvent({}),
			topicId,
			topicName,
		});
	};

	const onSpaceChanged = (newValue: ValueType<OptionType>, actionMeta: ActionMeta<OptionType>) => {
		if (actionMeta.action === 'select-option' && newValue) {
			const selectedValue = newValue.value;
			updateSelectedSpaceKey(selectedValue);
		}
	};

	const options = editableSpaces.map(({ spaceKey, spaceName }) => ({
		label: spaceName,
		value: spaceKey,
	}));

	return (
		<ContextualAnalyticsData sourceType={MODAL} sourceName="createArticle">
			<ModalTransition>
				<ShortcutScope>
					<AkModalDialog
						autoFocus
						onClose={onCancel}
						shouldCloseOnOverlayClick={!isFetchingDraftDetails}
						shouldCloseOnEscapePress={!isFetchingDraftDetails}
						width={MODAL_WIDTH}
					>
						<>
							<ModalHeader>
								<ModalTitle>{formatMessage(messages.heading)}</ModalTitle>
							</ModalHeader>
							<ModalBody>
								<DropdownText htmlFor="space-name-dropdown-field">
									{formatMessage(messages.selectLabel)}
								</DropdownText>
								<Select
									placeholder={formatMessage(messages.selectPlaceholder)}
									options={options}
									onChange={onSpaceChanged}
									// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
									menuPortalTarget={document.body}
									styles={{
										menuPortal: (base) => ({
											...base,
											zIndex: 9999,
										}),
									}}
									autoFocus
									isDisabled={isFetchingDraftDetails}
									inputId="space-name-dropdown-field"
								/>
							</ModalBody>
							<ModalFooter>
								<Button appearance="subtle" onClick={onCancel} isDisabled={isFetchingDraftDetails}>
									{formatMessage(messages.cancelBtnText)}
								</Button>
								<LoadingButton
									appearance="primary"
									onClick={fetchDraftAndNavigateToRoute}
									isLoading={isFetchingDraftDetails}
									isDisabled={!selectedSpaceKey.length}
								>
									{formatMessage(messages.nextBtnText)}
								</LoadingButton>
							</ModalFooter>
						</>
					</AkModalDialog>
				</ShortcutScope>
			</ModalTransition>
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};

const dropdownTextStyles = css({
	display: 'block',
	marginTop: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N400),
	textTransform: 'none',
	marginBottom: token('space.100', '8px'),
});

const DropdownText = (props: { children: React.ReactNode; htmlFor: string }) => (
	<AtlaskitHeading size="xxsmall">
		<label htmlFor={props.htmlFor} css={dropdownTextStyles}>
			{props.children}
		</label>
	</AtlaskitHeading>
);
